import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';

import {IdentifyList} from '../../interfaces/IdentifyList';
import {IdentifyDisplay} from '../../interfaces/IdentifyDisplay';
import {ConfigService} from '../config/config.service';

@Injectable({
  providedIn: 'root'
})
export class IdentifyParseService {


  constructor(private http: HttpClient, private config: ConfigService) {}

  /**
   * Checks formatting for attributes of server queries
   * It looks at the attribute type from the service query and then parses it accordingly
   * It then looks for any config attribute types and then parses and formats accordingly
   */
  public formatCheck(fields: any, attributes: any, formatFields?: any) {
    // checks for fields to format
    let formatFieldsKeys;
    if (formatFields) {
      formatFieldsKeys = Object.keys(formatFields);
    }

    for (const field of fields) {
      // if date field
      if (field.type === 'esriFieldTypeDate') {
        if (attributes[field.name]) {
          const date: any = new Date(attributes[field.name]);
          //date.setDate(date.getDate() + 1);
          attributes[field.name] =
            (date.getMonth() + 1) + '/' + date.getDate() + '/' + date.getFullYear();
        }
      }
      if (field.type === 'esriFieldTypeInteger') { attributes[field.name] = parseInt(attributes[field.name], 10); }
      if (field.type === 'esriFieldTypeDouble' && attributes[field.name]) { attributes[field.name] = parseFloat(attributes[field.name]); }
      // checks for any number fields to format
      if (formatFieldsKeys) {
        for (const key of formatFieldsKeys) {
          if (field.name === key) {
            if (attributes[field.name]) {
              if (typeof formatFields[field.name] === 'number') {
                // checks to see if the subscription has been canceled in order to abort function
                if (attributes[field.name].isNaN) {
                  attributes[field.name] = null;
                } else {
                  const numberParse = parseFloat(attributes[field.name]);
                  attributes[field.name] =
                    parseFloat(numberParse.toFixed(formatFields[field.name]));
                }
              }
              if (formatFields[field.name] === 'wtmfPDFTransformPipe' && this.config && 
                this.config.WtmfPDFInfo && this.config.WtmfPDFInfo.DNRWtmfPDFUrl && 
                this.config.WtmfPDFInfo.DNRWtmfPDFOSPath) {
                // Update the field value to the URL of the PDF rather than the file system path
                //WtmfPDFInfo: property that stores the url && OS paths to the WTMF pdf's
                const wtmfUrlPath = this.config.WtmfPDFInfo.DNRWtmfPDFUrl;
                const wtmfOSPath = this.config.WtmfPDFInfo.DNRWtmfPDFOSPath;
                let value = attributes[field.name];
                if (value.toString().indexOf(wtmfOSPath) != -1) {
                  let wtmfFilename = value.slice(value.length - 17, value.length - 4);
                  if (wtmfFilename && wtmfFilename.length > 0) {
                    attributes[field.name] = wtmfUrlPath + wtmfFilename;
                  }
                }
              }
            }
          }
        }
      }
    }
    return attributes;
  }

  /**
   * When an identify query is run, this function routes the return to the right parsing function or returns a default null object
   */
  public async parseIdentify(result) {
    if (result && result.queryResults) {
      if (!result.queryResults.errorMessage) {
        if (result.queryTaskObj.queryID === 'identifyFeatures') {
          return this.identifyFeatures(result);
        } else if (result.queryTaskObj.queryID === 'identifyRelateFeatures') {
          return this.identifyRelatedFeatures(result);
        }
      } else {
        // controls the error message for the identify widget
        let msg = 'Error: ' + result.queryResults.errorMessage;
        const data: any = await this.http.get(result.queryTaskObj.layerTaskUrl + '?f=pjson').toPromise();
        if (data) {
          if (data.type === 'Raster Layer') {
            msg = 'This raster layer does not have identify enabled.';
          }
        }
        return {
          accordion: false,
          columns: {
            names: [['identify']],
            aliases: [['Note']],
            types: [['string']]
          },
          header: [result.queryTaskObj.name],
          name: result.queryTaskObj.name,
          displayFieldName: 'identify',
          highlight: null,
          featureArr: [[[msg]]],
          geometryArr: [],
          geometryType: null,
          url: result.queryTaskObj.layerTaskUrl,
          formatFields: null,
          relateObj: null,
          parent: result.queryTaskObj.parent,
          identifyNumber: result.queryTaskObj.identifyNumber,
          identifyType: null,
          list: ['--'],
          objectids: null
        };
      }
    }
  }

  /**
   * Parses an identify query return when it is an esri service
   * @param result: query service result
   */
  private identifyFeatures(result) {
    const serverQuery = result.queryResults.featureSet;
    const rows = [];
    const objectids = [];
    const list = [];
    const geometry = [];
    let columns = {
      names: [],
      aliases: [],
      types: []
    };
    const queryTaskObj = result.queryTaskObj;
    let headers = [];
    const geometryType = serverQuery.geometryType;
    if (serverQuery.features.length > 0) {
      if (!queryTaskObj.displayFieldName) {
        queryTaskObj.displayFieldName = result.queryResults.featureSet.displayFieldName;
      }
      columns = this.getColumns(serverQuery);
      if (queryTaskObj.identifyType) {
        // checks to see if current layer should have subheadings
        // and parses the attributes into sub arrays accordingly
        if (queryTaskObj.identifyType.type === 'subheads') {
          headers = queryTaskObj.identifyType.headers;
          const groupAliases = [];
          const groupTypes = [];
          for (const feature of serverQuery.features) {
            const groups = [];
            const geom = feature.geometry;
            let attributes = Object.assign({}, feature.attributes);
            attributes = this.formatCheck(
              serverQuery.fields, attributes, result.queryTaskObj.formatFields);
            for (const group of queryTaskObj.identifyType.groups) {
              const subGroups = [];
              const subGroupsAliases = [];
              const subGroupTypes = [];
              for (const item of group) {
                if (item === 'OBJECTID') {
                  objectids.push(attributes[item]);
                } else {
                  subGroups.push(attributes[item]);
                }
                if (item === queryTaskObj.displayFieldName) {
                  list.push(attributes[item]);
                }
                for (const field of serverQuery.fields) {
                  if (field.name === item) {
                    subGroupsAliases.push(field.alias);
                  }
                  subGroupTypes.push(field.type);
                }
              }
              groups.push(subGroups);
              groupAliases.push(subGroupsAliases);
              groupTypes.push(subGroupTypes);
            }
            geometry.push(geom);
            rows.push(groups);
          }
          columns = {
            names: queryTaskObj.identifyType.groups,
            aliases: groupAliases,
            types: groupTypes
          };
        }
      } else {
        // formats the identify object with default one heading and list of everything else
        headers = ['Feature Information'];
        // parses out the attributes/geometry for the table object
        for (const queryFeature of serverQuery.features) {
          let featureAttr = Object.assign({}, queryFeature.attributes);
          const feature = [];

          featureAttr = this.formatCheck(serverQuery.fields, featureAttr, result.queryTaskObj.formatFields);
          for (const key in featureAttr) {
            if (featureAttr.hasOwnProperty(key)) {
              feature.push(featureAttr[key]);
              if (key === 'OBJECTID') {
                objectids.push(featureAttr[key]);
              }
              if (key === queryTaskObj.displayFieldName) {
                list.push(featureAttr[key]);
              }
            }
          }
          rows.push([feature]);
          if (queryFeature.geometry) {
            geometry.push(queryFeature.geometry);
          }
        }
      }
      // creates the identify array
      const newIdentifyList: IdentifyList = {
        columns,
        header: headers,
        name: queryTaskObj.name,
        displayFieldName: queryTaskObj.displayFieldName,
        highlight: null,
        featureArr: rows,
        geometryArr: geometry,
        geometryType,
        url: queryTaskObj.layerTaskUrl,
        formatFields: queryTaskObj.formatFields,
        relateObj: queryTaskObj.relateObj,
        parent: queryTaskObj.parent,
        identifyNumber: queryTaskObj.identifyNumber,
        identifyType: queryTaskObj.identifyType,
        list,
        objectids
      };
      // pings the observable that something has changed
      return newIdentifyList;
    } else {
      return 'blank';
    }
  }

  /**
   * Formats raster identify results
   * @param result: query result
   */
  public formatRasterIdentify(result) {
    if (result) {
      const attributes = [];
      let listVal = '';
      for (const key in result[0].attributes) {
        if (result[0].attributes.hasOwnProperty(key)) {
          attributes.push(result[0].attributes[key]);
          if (key === 'Value') {
            listVal = result[0].attributes[key];
          }
        }
      }
      const identifyArr: IdentifyList = {
        columns: {
          names: [Object.keys(result[0].attributes)],
          aliases: [Object.keys(result[0].attributes)],
          types: [Object.keys(result[0].attributes)]
        },
        header: [result[0].layerName],
        name: result[0].layerName,
        displayFieldName: '',
        highlight: null,
        featureArr: [[attributes]],
        geometryArr: null,
        geometryType: null,
        url: null,
        formatFields: null,
        relateObj: null,
        parent: null,
        identifyNumber: result[2],
        identifyType: null,
        list: [listVal],
        objectids: null
      };
      return identifyArr;
    }
  }


  /**
   * Parses query service results for related queries for the identify tool
   * @param result: query service result
   */
  public identifyRelatedFeatures(result) {
    const columns = this.getColumns(result.queryResults.featureSet);
    const rows = [];
    const slides = [];
    let image = '';
    const tableColumns = [];
    let tableRows = [];
    const tmpRows = [];
    let listGalleryIndex = null;

    // automatically sorts on date fields
    if (result.queryResults.featureSet &&  result.queryResults.featureSet.relatedRecordGroups) {
      if (result.queryResults.featureSet.relatedRecordGroups[0]) {
        if (result.queryResults.featureSet.relatedRecordGroups[0].relatedRecords) {
          let array = result.queryResults.featureSet.relatedRecordGroups[0].relatedRecords;
          array = array.sort((a: any, b: any) => {
            const c: any = new Date(a.attributes[result.queryTaskObj.orderByFields]);
            const d: any = new Date(b.attributes[result.queryTaskObj.orderByFields]);
            if (c === d) {
              return 0;
            }
            if (!c || c < d) {
              return -1;
            }
            if (!d || c > d) {
              return 1;
            }
            return c - d;
          });
          result.queryResults.featureSet.relatedRecordGroups[0].relatedRecords = array;
        }
      }

      for (const relatedGroup of result.queryResults.featureSet.relatedRecordGroups) {
        for (const record of relatedGroup.relatedRecords) {
          let item = Object.assign({}, record.attributes);
          const feature = [];
          item = this.formatCheck(result.queryResults.featureSet.fields, item, result.queryTaskObj.formatFields);
          tmpRows.push(item);
          for (const key in item) {
            if (item.hasOwnProperty(key)) {
              feature.push(item[key]);
            }
          }
          rows.push([feature]);
        }
      }
      let identityType = 1;
      if (result.queryTaskObj.identifyType === 'list') {
        identityType = 1;
      } else if (result.queryTaskObj.identifyType === 'gallery') {
        if (result.queryResults.featureSet.relatedRecordGroups[0]
          && result.queryResults.featureSet.relatedRecordGroups[0].relatedRecords[0]) {
          identityType = 2;
          for (let i = 0; i < rows.length; i++) {
            let active = false;
            let tmp = {};
            if (i === 0) {
              active = true;
            }
            tmp = {image:
                result.queryResults.featureSet.relatedRecordGroups[0].relatedRecords[i].attributes.PHOTO_LINK
                || result.queryResults.relatedRecordGroups[0].relatedRecords[i].attributes.URL,
              text: rows[i][0], active, index: i, fields: columns.aliases[0]};
            slides.push(tmp);
          }
        }
      } else if (result.queryTaskObj.identifyType === 'listGallery') {
        identityType = 3;
        listGalleryIndex = 1;
      } else if (result.queryTaskObj.identifyType === 'borehole') {
        identityType = 4;
        if (result.queryResults.featureSet.relatedRecordGroups[0]
          && result.queryResults.featureSet.relatedRecordGroups[0].relatedRecords[0]) {
          image = result.queryResults.featureSet.relatedRecordGroups[0].relatedRecords[0].attributes.URL;
        }
      } else if (result.queryTaskObj.identifyType === 'table') {
        identityType = 5;
        if (columns.names && tmpRows) {
          for (let i = 0; i < columns.names[0].length; i++) {
            tableColumns.push({headerName: columns.aliases[0][i], field: columns.names[0][i], sortable: true, filter: true});
          }
          tableRows = tmpRows;
          if (tableRows[0]) {
            if (tableRows[0].LAYER_NUMBER) {
              tableRows.sort((a: any, b: any) => a.LAYER_NUMBER - b.LAYER_NUMBER);
            }
          }
        }
      }
      const relateDisplay: IdentifyDisplay = {
        attributes: rows,
        columns,
        headers: [result.queryTaskObj.name],
        slides,
        identifyType: identityType,
        tableRows,
        tableColumns,
        borehole: image,
        identifyNumber: result.queryTaskObj.identifyNumber,
        listGalleryIndex,
        identifyOrder: result.queryTaskObj.identifyOrder
      };
      return relateDisplay;
    } else {
      const relateDisplay: IdentifyDisplay = {
        attributes: [],
        columns: {
          names: [],
          aliases: [],
          types: [],
        },
        headers: [],
      };
      return relateDisplay;
    }
  }

  /**
   * Parses query result for column object
   */
  public getColumns(serverQuery) {
    const fields = [];
    const fieldAliases = [];
    const fieldTypes = [];
    if (serverQuery && serverQuery.fields) {     
      for (const field of serverQuery.fields) {
          fields.push(field.name);
          fieldAliases.push(field.alias);
          fieldTypes.push(field.type);
        }
    }
    else {
      console.log('serverQuery.fields doesnt exist.');
  
    }

    return  {
      names: [fields],
      aliases: [fieldAliases],
      types: [fieldTypes]
    };
  }
}
